
import React from 'react';
import { UseFormReturn } from "react-hook-form";
import { 
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { CampaignFormValues } from "./types/formTypes";
import AIQuizGenerator from "../AIQuizGenerator";
import QuestionEditor from "../QuestionEditor";
import { ModalCampaignQuestionFormData } from "@/types/modalCampaigns";

interface QuizSettingsSectionProps {
  form: UseFormReturn<CampaignFormValues>;
  questions: ModalCampaignQuestionFormData[];
  onQuestionsGenerated: (questions: ModalCampaignQuestionFormData[]) => void;
  onAddQuestion: () => void;
  onUpdateQuestion: (index: number, question: ModalCampaignQuestionFormData) => void;
  onRemoveQuestion: (index: number) => void;
}

const QuizSettingsSection: React.FC<QuizSettingsSectionProps> = ({ 
  form,
  questions,
  onQuestionsGenerated,
  onAddQuestion,
  onUpdateQuestion,
  onRemoveQuestion
}) => {
  return (
    <div className="space-y-4 border rounded-lg p-4">
      <h3 className="text-lg font-medium">Quiz instellingen</h3>
      
      {/* AI Quiz Generator */}
      <AIQuizGenerator
        campaignTitle={form.getValues("title")}
        campaignContent={form.getValues("content")}
        onQuestionsGenerated={onQuestionsGenerated}
      />
      
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          control={form.control}
          name="min_score"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Minimale score</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  min="1"
                  {...field}
                  onChange={e => field.onChange(parseInt(e.target.value) || 1)}
                />
              </FormControl>
              <FormDescription>
                Minimaal benodigde punten om de quiz te halen
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="max_attempts"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Maximum pogingen</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  placeholder="Onbeperkt"
                  min="1"
                  {...field}
                  value={field.value || ""}
                  onChange={e => {
                    const value = e.target.value;
                    field.onChange(value ? parseInt(value) : undefined);
                  }}
                />
              </FormControl>
              <FormDescription>
                Laat leeg voor onbeperkte pogingen
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      <FormField
        control={form.control}
        name="repeat_on_failure"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <FormLabel className="text-base">Herhalen bij falen</FormLabel>
              <FormDescription>
                Quiz opnieuw aanbieden als de gebruiker niet slaagt?
              </FormDescription>
            </div>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />
      
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-md font-medium">Vragen</h4>
          <Button type="button" variant="outline" size="sm" onClick={onAddQuestion}>
            <Plus className="h-4 w-4 mr-1" /> Vraag toevoegen
          </Button>
        </div>
        
        {questions.length === 0 ? (
          <div className="text-center py-8 border rounded-md bg-muted/20">
            <p className="text-muted-foreground mb-2">Nog geen vragen toegevoegd</p>
            <Button type="button" variant="outline" onClick={onAddQuestion}>
              <Plus className="h-4 w-4 mr-1" /> Eerste vraag toevoegen
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {questions.map((question, index) => (
              <div key={index} className="border rounded-md p-4">
                <div className="flex justify-between items-center mb-4">
                  <h5 className="font-medium">Vraag {index + 1}</h5>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveQuestion(index)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-red-500"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                  </Button>
                </div>
                
                <QuestionEditor
                  question={question}
                  onChange={(updatedQuestion) => onUpdateQuestion(index, updatedQuestion)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizSettingsSection;
